import {
  SkyButton,
  SkyDialog,
  SkyDropdown,
  SkyInput,
  SkyMessageBar,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import { useLocation } from "wouter";
import type { DestinationType, InstanceAdminView } from "@shared/types/instancesAdmin";
import { slugSchema, type TenantView } from "@shared/types/tenants";
import { tenantGap } from "@/hooks/use-tenants";
import { NEW_TENANT_ADDRESS, ROUTES, absolutePath } from "@/lib/routes";
import { ErrorState } from "./ScreenState";

/** #921: said in place of an empty Tenant list when there is no tenant at all. */
export const NO_TENANT_FOR_INSTANCE_DIALOG =
  "An instance belongs to a tenant, and there is no tenant yet. Create a tenant first. Its first instance is created with it.";

/** #921 review: said in place of an empty Tenant list when every tenant is disabled. */
export const NO_ACTIVE_TENANT_DIALOG =
  "An instance belongs to a tenant, and every tenant is disabled. Enable one on Tenants first.";

/**
 * Create or edit an instance (FR-20, FR-22, canvas 12 panel 02).
 *
 * Creating is asynchronous and the button says so: "Create and provision",
 * because both happen and the second takes longer than the dialog stays open.
 *
 * ADR 0005: an instance lives in its own schema of its TENANT's database, so
 * there is no placement to choose on the create form any more: the tenant's
 * database was placed when the tenant was created. The edit form shows the
 * placement read-only (server, database and schema) beside the fields that
 * can change, the name and the environment label.
 */

const DESTINATION_OPTIONS = [
  { value: "snowflake", text: "Snowflake" },
  { value: "databricks", text: "Databricks" },
];

export interface InstanceFormValues {
  tenantId: string;
  name: string;
  slug: string;
  environmentLabel: string;
  destinationType: DestinationType;
}

interface FieldErrors {
  tenantId?: string;
  name?: string;
  slug?: string;
}

function tenantOptionsFor(tenants: TenantView[], instance: InstanceAdminView | null) {
  return (
    tenants
      .filter((tenant) => tenant.status === "active" || tenant.id === instance?.tenantId)
      // The tenant's Display name (round 6 A3; platform 0027 made it the tenant's own column).
      .map((tenant) => ({
        value: tenant.id,
        text: tenant.displayName,
      }))
  );
}

function seedValues(instance: InstanceAdminView | null, defaultTenantId: string | null) {
  return {
    tenantId: instance?.tenantId ?? defaultTenantId ?? "",
    name: instance?.name ?? "",
    slug: instance?.slug ?? "",
    environmentLabel: instance?.environmentLabel ?? "",
  };
}

function dialogCopy(instance: InstanceAdminView | null) {
  if (instance === null) {
    return {
      title: "New instance",
      description:
        "An instance is a separate workspace in this tenant, for example Development or Production, with its own connections, schedules and data.",
      primary: "Create instance",
    };
  }
  return {
    title: `Edit ${instance.name}`,
    description: "The slug and where the instance's data is kept are fixed once it is created.",
    primary: "Save changes",
  };
}

function validateInstanceForm(form: {
  isEdit: boolean;
  tenantId: string;
  name: string;
  slug: string;
}): FieldErrors {
  const next: FieldErrors = {};
  if (!form.isEdit && form.tenantId === "") {
    next.tenantId = "Choose the tenant this instance sits under.";
  }
  if (form.name.trim() === "") next.name = "Give the instance a name.";
  if (!form.isEdit) {
    const parsed = slugSchema.safeParse(form.slug);
    if (!parsed.success) next.slug = parsed.error.issues[0]?.message ?? "That slug is not valid.";
  }
  return next;
}

export function InstanceDialog({
  open,
  onOpenChange,
  instance,
  tenants,
  defaultTenantId,
  onSubmit,
  isSaving,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Null creates; a row edits. */
  instance: InstanceAdminView | null;
  tenants: TenantView[];
  defaultTenantId: string | null;
  onSubmit: (values: InstanceFormValues) => void;
  isSaving: boolean;
  error: unknown;
}) {
  const isEdit = instance !== null;
  const [, navigate] = useLocation();
  // Seeded once per opening; the caller remounts this component with a key.
  const seed = seedValues(instance, defaultTenantId);
  const [tenantId, setTenantId] = useState(seed.tenantId);
  const [name, setName] = useState(seed.name);
  const [slug, setSlug] = useState(seed.slug);
  const [environmentLabel, setEnvironmentLabel] = useState(seed.environmentLabel);
  const [destinationType, setDestinationType] = useState<DestinationType>("snowflake");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const tenantOptions = tenantOptionsFor(tenants, instance);
  const copy = dialogCopy(instance);

  function submit() {
    const next = validateInstanceForm({ isEdit, tenantId, name, slug });
    if (Object.keys(next).length > 0) {
      setFieldErrors(next);
      return;
    }
    setFieldErrors({});
    onSubmit({
      tenantId,
      name: name.trim(),
      slug: slug.trim(),
      environmentLabel: environmentLabel.trim(),
      destinationType,
    });
  }

  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      title={copy.title}
      description={copy.description}
      size="large"
      testId="instance-dialog"
      // SkyDialog closes itself on a primary action, which would throw away a
      // form the server has just refused. The dialog closes when the mutation
      // succeeds, and only then; a failure leaves the typing where it is.
      onBeforeClose={(reason) => reason !== "primaryAction"}
      primaryAction={{
        label: copy.primary,
        onClick: submit,
        loading: isSaving,
        disabled: !isEdit && tenantOptions.length === 0,
      }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false) }}
    >
      <div className="flex flex-col gap-4">
        {error ? <ErrorState title="That instance was not saved" error={error} /> : null}

        {isEdit ? null : (
          <TenantField
            tenants={tenants}
            tenantOptions={tenantOptions}
            tenantId={tenantId}
            error={fieldErrors.tenantId}
            onSelect={setTenantId}
            navigate={navigate}
          />
        )}

        <SkyInput
          label="Name"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
          hint="Shown in the instance switcher."
          validationState={fieldErrors.name ? "error" : "none"}
          validationMessage={fieldErrors.name}
          testId="instance-name"
        />

        <SkyInput
          label="Slug"
          required={!isEdit}
          readOnly={isEdit}
          value={slug}
          onChange={(event) => setSlug(event.target.value)}
          hint={
            isEdit
              ? "Set at creation. It appears in URLs and in the instance switcher."
              : "Lower case, letters, digits and hyphens, for example prod or sandbox."
          }
          validationState={fieldErrors.slug ? "error" : "none"}
          validationMessage={fieldErrors.slug}
          testId="instance-slug"
        />

        <SkyInput
          label="Environment label"
          value={environmentLabel}
          onChange={(event) => setEnvironmentLabel(event.target.value)}
          hint="What the switcher shows beside the name, for example Production."
          testId="instance-environment-label"
        />

        {instance ? <PlacementSection instance={instance} /> : null}

        {isEdit ? null : (
          <CreateOnlyFields destinationType={destinationType} onDestination={setDestinationType} />
        )}
      </div>
    </SkyDialog>
  );
}

function TenantField({
  tenants,
  tenantOptions,
  tenantId,
  error,
  onSelect,
  navigate,
}: {
  tenants: TenantView[];
  tenantOptions: { value: string; text: string }[];
  tenantId: string;
  error: string | undefined;
  onSelect: (id: string) => void;
  navigate: (to: string) => void;
}) {
  if (tenantOptions.length > 0) {
    return (
      <SkyDropdown
        label="Tenant"
        required
        options={tenantOptions}
        selectedOptions={tenantId ? [tenantId] : []}
        placeholder="Choose a tenant"
        onOptionSelect={(values) => onSelect(values[0] ?? "")}
        validationState={error ? "error" : "none"}
        validationMessage={error ?? "Only active tenants take new instances."}
        testId="instance-tenant"
      />
    );
  }
  // #921: never an empty list. Instances live in a tenant: with none at all the way on is
  // New tenant, with only disabled ones it is Tenants, where one is enabled.
  const none = tenantGap(tenants) === "none";
  return (
    <SkyMessageBar
      intent="warning"
      layout="multiline"
      title={none ? "No tenants yet" : "No active tenant"}
      message={none ? NO_TENANT_FOR_INSTANCE_DIALOG : NO_ACTIVE_TENANT_DIALOG}
      actions={
        <SkyButton
          appearance="secondary"
          size="small"
          testId="instance-create-tenant"
          onClick={() => navigate(absolutePath(none ? NEW_TENANT_ADDRESS : ROUTES.adminTenants))}
        >
          {none ? "Create a tenant" : "Open Tenants"}
        </SkyButton>
      }
      testId="instance-no-tenant"
    />
  );
}

/** #1191: only a PlatformAdmin's answer carries the placement; Edit is theirs alone. */
function PlacementSection({ instance }: { instance: InstanceAdminView }) {
  if (instance.tenantDbServer === undefined || instance.tenantDbName === undefined) return null;
  return (
    <div className="flex flex-col gap-2">
      <SkyText as="h3" size={300} weight="semibold" block>
        Database placement
      </SkyText>
      <SkyInput
        label="PostgreSQL server"
        readOnly
        value={instance.tenantDbServer}
        testId="instance-db-server"
      />
      <SkyInput
        label="Database name"
        readOnly
        value={instance.tenantDbName}
        testId="instance-db-name"
      />
      <SkyInput
        label="Schema"
        readOnly
        value={instance.tenantDbSchema ?? "public"}
        // copy-jargon-ok: the read-only placement facts a support person asks for.
        hint="The instance's own schema in its tenant's database."
        testId="instance-db-schema"
      />
    </div>
  );
}

function CreateOnlyFields({
  destinationType,
  onDestination,
}: {
  destinationType: DestinationType;
  onDestination: (value: DestinationType) => void;
}) {
  return (
    <>
      <SkyDropdown
        label="Destination"
        options={DESTINATION_OPTIONS}
        selectedOptions={[destinationType]}
        onOptionSelect={(values) => onDestination((values[0] as DestinationType) ?? "snowflake")}
        validationMessage="Recorded now. The connection details are set on the Destination screen."
        testId="instance-destination"
      />

      <SkyText size={200} color="muted" block testId="instance-placement-note">
        The instance keeps its data apart from the tenant&apos;s other instances.
      </SkyText>

      <SkyMessageBar
        intent="info"
        layout="multiline"
        title="Setting up takes a few minutes"
        message="skyData sets up the new instance in the background, usually in 2 to 3 minutes. Its row shows the progress and turns Active when it is ready. You can close this window. Setup carries on."
      />
    </>
  );
}
